import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const apiBase = import.meta.env.VITE_API_BASE;

export default function Contact() {
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const form = e.currentTarget;
        const data = {
            name: (form.elements.namedItem("name") as HTMLInputElement).value,
            email: (form.elements.namedItem("email") as HTMLInputElement).value,
            company: (form.elements.namedItem("company") as HTMLInputElement).value,
            message: (form.elements.namedItem("message") as HTMLTextAreaElement).value,
        };

        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (res.ok) {
                setSent(true);
            } else {
                console.error("API error:", await res.text());
                alert("Something went wrong, please try again later.");
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the server.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <main className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-950 to-black text-gray-100 pt-28 pb-20">
            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-12">
                {/* ---------------- Left Text + Form ---------------- */}
                <div className="flex-1 z-10">
                    <h1 className="text-4xl font-bold text-white">Contact Us</h1>
                    <p className="mt-4 text-lg text-gray-300 leading-relaxed">
                        Tell us a bit about your use case and we’ll reach out to discuss how SecureID can help.
                    </p>

                    {!sent ? (
                        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-sm font-semibold text-gray-300">Name</label>
                                <input
                                    name="name"
                                    required
                                    className="mt-2 w-full rounded-lg border border-gray-700 bg-slate-800 text-gray-100 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-300">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    className="mt-2 w-full rounded-lg border border-gray-700 bg-slate-800 text-gray-100 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-300">Company / Agency</label>
                                <input
                                    name="company"
                                    className="mt-2 w-full rounded-lg border border-gray-700 bg-slate-800 text-gray-100 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-300">Message</label>
                                <textarea
                                    name="message"
                                    rows={5}
                                    required
                                    className="mt-2 w-full rounded-lg border border-gray-700 bg-slate-800 text-gray-100 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-xl bg-indigo-600 px-5 py-3 text-white font-semibold hover:bg-indigo-700 transition shadow-lg"
                            >
                                {loading ? "Sending..." : "Send Message"}
                            </button>
                            <p className="text-xs text-gray-400">
                                Or email us directly at{" "}
                                <a
                                    href="mailto:info@secureidtechnologies.com"
                                    className="text-indigo-400 hover:underline"
                                >
                                    info@secureidtechnologies.com
                                </a>
                            </p>
                        </form>
                    ) : (
                        <div className="mt-8 rounded-xl border border-green-300 bg-green-900/20 p-4 text-green-300">
                            ✅ Thanks! We’ll get back to you shortly.
                        </div>
                    )}

                    {/* Back link */}
                    <p className="mt-10 text-center lg:text-left text-sm text-gray-400">
                        <Link to="/" className="text-indigo-400 hover:underline">
                            ← Back to Home
                        </Link>
                    </p>
                </div>

                {/* ---------------- Right Animated Image ---------------- */}
                <motion.div
                    className="flex-1 flex justify-center"
                    initial={{ opacity: 0, x: 150 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.1, ease: "easeOut" }}
                >
                    <img
                        src="/Images/contact-secureid.jpg"
                        alt="Contact illustration"
                        className="w-[90%] max-w-md rounded-2xl shadow-2xl border border-gray-800"
                    />
                </motion.div>
            </section>

            {/* Optional background accent */}
            <div className="absolute inset-0 bg-gradient-to-t from-indigo-900/10 to-transparent pointer-events-none"></div>
        </main>
    );
}
